import React, { useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import H from '@here/maps-api-for-javascript';
import getCountryISO2 from 'country-iso-3-to-2';
import styled from '@emotion/styled';
import { isEqual } from 'lodash';
import { fitBoundsFromBbox } from 'components/Map/NewJobMarkers/utils/markerHelpers';
import withTranslation from '../../hoc/withTranslation';
import { TIMEOUT_VALUES } from '../../actions';
import '@here/maps-api-for-javascript/bin/mapsjs-ui.css';
import {
  createBaseLayers,
  createMap,
  createSettingsControl,
  createUI,
  setUnitSystem,
} from '../../utils/map/HereMapUtils';
import { getBounds } from '../../utils/map/MapCalculations';
import NewMapMarker from './MapMarkers/NewMapMarker';
import HereLocationSelector from './HereLocationSelector';
import NewMapTourLines from './NewTourLines';

const StyledMap = styled.div({
  width: '100%',
  overflow: 'hidden',
  position: 'fixed',
  height: 'calc(100vh - 3rem)',
});

const engineType = H.Map.EngineType.HARP;

const NewMap = ({ user, oAuth, display, mapData }) => {
  const mapRef = useRef(null);
  const mapUIRef = useRef(null);
  const settingsControlRef = useRef(null);
  const map = useRef(null);
  const boundsTimerRef = useRef(null);
  const prevBoundsRef = useRef(null);
  const currentStep = useSelector((state) => state.usageContext.currentStep);

  const ISO2Code = getCountryISO2(user.politicalView);
  const mapLayers = useMemo(
    () => createBaseLayers(engineType, user.language, ISO2Code),
    [user.language, ISO2Code],
  );

  useEffect(() => {
    if (!map.current) {
      map.current = createMap(mapLayers, mapRef, engineType);
      const settingsControl = createSettingsControl(mapLayers);
      mapUIRef.current = createUI(mapLayers, map.current, settingsControl);
      settingsControlRef.current = settingsControl;
      setUnitSystem(mapUIRef.current, user.distance);
    }
  }, [mapLayers, user.distance]);

  useEffect(() => {
    return () => {
      if (map.current?._resizeHandler) {
        window.removeEventListener('resize', map.current._resizeHandler);
      }
    };
  }, []);

  useEffect(() => {
    if (mapUIRef.current) setUnitSystem(mapUIRef.current, user.distance);
  }, [user.distance]);

  useEffect(() => {
    if (map.current && !display.noMapMovement) {
      const newBounds = getBounds(mapData, user, display, null);

      if (!isEqual(newBounds, prevBoundsRef.current)) {
        prevBoundsRef.current = newBounds;

        if (boundsTimerRef.current) clearTimeout(boundsTimerRef.current);

        boundsTimerRef.current = setTimeout(() => {
          fitBoundsFromBbox(map.current, newBounds);
          boundsTimerRef.current = null;
        }, TIMEOUT_VALUES.HIGH);
      }
    }

    return () => {
      if (boundsTimerRef.current) clearTimeout(boundsTimerRef.current);
    };
  }, [
    mapData,
    display.showProblem,
    display.showUnassigned,
    display.selectedTour,
    display.noMapMovement,
    display.routeIds,
  ]);

  return (
    <StyledMap ref={mapRef}>
      {map.current && (
        <>
          <NewMapMarker
            display={display}
            map={map.current}
            mapData={mapData}
            ui={mapUIRef}
            user={user}
            oAuth={oAuth}
          />
          <HereLocationSelector
            map={map.current}
            oAuth={oAuth}
            display={display}
            allowRightClick={currentStep === 2}
          />
          <NewMapTourLines map={map.current} user={user} oAuth={oAuth} mapData={mapData} />
        </>
      )}
    </StyledMap>
  );
};

export default withTranslation(NewMap);
